// Kiosk route entry point + tab-based ticket workspace (S67d Phase 2).
//
// Screen hierarchy:
//   loading                          → spinner
//   anonymous + no weighbridge       → password login
//   authenticated + no weighbridge   → weighbridge selector
//   authenticated + weighbridge      → KioskShell (tab manager)
//
// Tab architecture:
//   - Eager ticket creation: POST /wb/tickets when customer picked.
//   - One WbTicketProvider per open tab.
//   - Hidden tabs use display:none (not unmounted) to preserve React state.
//   - BridgeSelectorRow and bridge state live at KioskShell level (shared).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useKioskStore } from '../../../store/kioskStore';
import { useWbWeighbridge } from '../hooks/useWbWeighbridges';
import { fetchWbProducts, fetchWbVatRates } from '../hooks/useWbProducts';
import { WbLiveWeightProvider, useWbLiveWeight } from '../context/WbLiveWeightContext';
import { WbTicketProvider } from '../context/WbTicketContext';
import { useWbLiveBridges } from '../../../hooks/wb/useWbLiveBridges';
import { useActiveBridge } from '../../../hooks/wb/useActiveBridge';
import { BridgeSelectorRow } from '../../../components/wb/BridgeSelectorRow';
import { KioskLayout, KIOSK_SIMULATOR_MODE } from './KioskLayout';
import { KioskLoginScreen } from './KioskLoginScreen';
import { KioskWeighbridgeSelector } from './KioskWeighbridgeSelector';
import { KioskTicketList } from './KioskTicketList';
import { KioskTicketWorkspace } from './KioskTicketWorkspace';
import { KioskCustomerPickerModal, fetchKioskCustomers } from './KioskCustomerPickerModal';
import { KioskTicketPickerModal } from './KioskTicketPickerModal';
import { KioskSettings } from './KioskSettings';
import { KioskTabBar } from './KioskTabBar';
import { useKioskTabs } from './hooks/useKioskTabs';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { TicketDetailDto } from '../hooks/useWbTicket';
import styles from './kiosk.module.css';

/**
 * Authenticated + weighbridge-locked. Hosts WbLiveWeightProvider (shared
 * across all tabs). WbTicketProvider is created per-tab inside KioskShell.
 */
function KioskAuthedSession({
  weighbridgeEncId,
  staffName,
  onLogout,
  onChangeWeighbridge,
}: {
  weighbridgeEncId: string;
  staffName: string | null;
  onLogout: () => void;
  onChangeWeighbridge: () => void;
}) {
  const { data } = useWbWeighbridge(weighbridgeEncId);
  const wb = data?.weighbridge;
  const name = wb?.name ?? null;
  const yardEncId = wb?.yard.enc_id ?? null;

  return (
    <WbLiveWeightProvider>
      <KioskShell
        weighbridgeEncId={weighbridgeEncId}
        weighbridgeName={name}
        yardEncId={yardEncId}
        staffName={staffName}
        onLogout={onLogout}
        onChangeWeighbridge={onChangeWeighbridge}
      />
    </WbLiveWeightProvider>
  );
}

/**
 * Tab manager. Owns bridge selection (shared across all tabs), tab state
 * (useKioskTabs), and the customer picker modal for new ticket creation.
 */
function KioskShell({
  weighbridgeEncId,
  weighbridgeName,
  yardEncId,
  staffName,
  onLogout,
  onChangeWeighbridge,
}: {
  weighbridgeEncId: string;
  weighbridgeName: string | null;
  yardEncId: string | null;
  staffName: string | null;
  onLogout: () => void;
  onChangeWeighbridge: () => void;
}) {
  const { setLiveWeight } = useWbLiveWeight();

  const [screen, setScreen] = useState<'list' | 'settings'>('list');
  const [pickingCustomer, setPickingCustomer] = useState(false);
  const [pickingExisting, setPickingExisting] = useState(false);
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [tabLabels, setTabLabels] = useState<Record<string, string>>({});

  const { tabs, activeTab, activeLinePerTab, openTab, closeTab, activateTab, setActiveLine } =
    useKioskTabs();

  // Bridge selection — shared across all tabs.
  const { data: bridgesData } = useWbLiveBridges();
  const bridges = bridgesData?.bridges ?? [];
  const { activeBridge, activeBridgeId, setActiveBridge } = useActiveBridge(bridges);

  const handleTabLabel = useCallback((encId: string) => (label: string) => {
    setTabLabels((prev) => ({ ...prev, [encId]: label }));
  }, []);

  const handleOpenExisting = useCallback(
    (encId: string) => {
      openTab(encId);
    },
    [openTab],
  );

  const handleCloseTab = useCallback(
    (encId: string) => {
      closeTab(encId);
    },
    [closeTab],
  );

  const handleHome = useCallback(() => {
    if (activeTab) closeTab(activeTab);
  }, [activeTab, closeTab]);

  // Eager ticket creation: POST before opening the tab.
  async function handleCustomerPicked(c: { encId: string; name: string; direction: 'inwards' | 'outwards' }): Promise<void> {
    if (!yardEncId) return; // weighbridge data not yet loaded
    setPickingCustomer(false);
    setCreatingTicket(true);
    try {
      const res = await apiClient.post<{ ticket: TicketDetailDto }>(endpoints.wb.tickets, {
        direction: c.direction,
        customer_enc_id: c.encId,
        weighbridge_enc_id: weighbridgeEncId,
        yard_enc_id: yardEncId,
        weighing_mode: 'two_pass',
      });
      const encId = res.data.ticket.enc_id;
      setTabLabels((prev) => ({ ...prev, [encId]: c.name }));
      openTab(encId);
    } catch {
      // Creation failed — stay on list; user can retry.
    } finally {
      setCreatingTicket(false);
    }
  }

  const setActiveLineHandlers = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((id) => [id, (uid: string | null) => setActiveLine(id, uid)]),
      ) as Record<string, (uid: string | null) => void>,
    [tabs, setActiveLine],
  );

  const onCloseHandlers = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((id) => [id, () => handleCloseTab(id)]),
      ) as Record<string, () => void>,
    [tabs, handleCloseTab],
  );

  const onTicketLabelHandlers = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((id) => [id, handleTabLabel(id)]),
      ) as Record<string, (label: string) => void>,
    [tabs, handleTabLabel],
  );

  const showList = screen === 'list' && activeTab === null;
  const showTabBar = tabs.length > 0;

  return (
    <KioskLayout
      weighbridgeName={weighbridgeName}
      staffName={staffName}
      onLogout={onLogout}
      onSettings={() => setScreen('settings')}
      onHome={handleHome}
      showHome={activeTab !== null}
    >
      {/* Sidebar + content horizontal split */}
      {bridges.length > 0 ? (
        <div className={styles.wbSidebar}>
          <BridgeSelectorRow
            bridges={bridges}
            activeBridgeId={activeBridgeId}
            onSelect={setActiveBridge}
            onActiveReading={(value, stable) => setLiveWeight(value, stable)}
            variant="sidebar"
          />
        </div>
      ) : null}

      <div className={styles.kioskContent}>
        {/* Tab bar — shown when at least one tab is open */}
        {showTabBar ? (
          <KioskTabBar
            tabs={tabs}
            activeTab={activeTab}
            ticketLabels={tabLabels}
            onActivate={activateTab}
            onClose={handleCloseTab}
            onNewTicket={() => setPickingCustomer(true)}
            onOpenExisting={() => setPickingExisting(true)}
          />
        ) : null}

        {/* Ticket list — visible when no tabs are active */}
        {showList ? (
          <KioskTicketList
            weighbridgeEncId={weighbridgeEncId}
            onNew={() => setPickingCustomer(true)}
            onOpen={handleOpenExisting}
          />
        ) : null}

        {/* Creating indicator */}
        {creatingTicket ? (
          <div className={styles.centered}>
            <p className={styles.muted}>Creating ticket…</p>
          </div>
        ) : null}

        {/* Workspace tabs — each in its own WbTicketProvider; hidden via display:none.
            Also hidden (not unmounted) while screen==='settings' so Settings doesn't
            render as a flex sibling on top of the active tab's workspace. */}
        {tabs.map((encId) => (
          <div
            key={encId}
            style={{
              display: encId === activeTab && screen === 'list' ? undefined : 'none',
              flex: '1 1 auto',
              minHeight: 0,
            }}
          >
            <WbTicketProvider>
              <KioskTicketWorkspace
                ticketEncId={encId}
                weighbridgeEncId={weighbridgeEncId}
                activeBridge={activeBridge}
                activeBridgeId={activeBridgeId}
                activeLineUid={activeLinePerTab[encId] ?? null}
                setActiveLine={setActiveLineHandlers[encId]!}
                onClose={onCloseHandlers[encId]!}
                onTicketLabel={onTicketLabelHandlers[encId]}
              />
            </WbTicketProvider>
          </div>
        ))}

        {/* Settings screen */}
        {screen === 'settings' ? (
          <KioskSettings
            weighbridgeName={weighbridgeName}
            simulator={KIOSK_SIMULATOR_MODE}
            onChangeWeighbridge={onChangeWeighbridge}
            onLogout={onLogout}
            onBack={() => setScreen('list')}
          />
        ) : null}
      </div>

      {/* Customer picker modal (new ticket) — fixed overlay, outside the split */}
      {pickingCustomer ? (
        <KioskCustomerPickerModal
          onCancel={() => setPickingCustomer(false)}
          onPicked={(c) => void handleCustomerPicked(c)}
        />
      ) : null}

      {/* Existing ticket picker modal */}
      {pickingExisting ? (
        <KioskTicketPickerModal
          onCancel={() => setPickingExisting(false)}
          onSelect={(encId, customerName) => {
            setPickingExisting(false);
            setTabLabels((prev) => ({ ...prev, [encId]: customerName }));
            openTab(encId);
          }}
        />
      ) : null}
    </KioskLayout>
  );
}

export function KioskRoot() {
  const status = useKioskStore((s) => s.status);
  const staff = useKioskStore((s) => s.staff);
  const weighbridgeEncId = useKioskStore((s) => s.weighbridgeEncId);
  const bootstrap = useKioskStore((s) => s.bootstrap);
  const logout = useKioskStore((s) => s.logout);
  const clearWeighbridge = useKioskStore((s) => s.clearWeighbridge);
  const queryClient = useQueryClient();

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  // Non-blocking login cache warm. The moment the kiosk session becomes
  // authenticated — via bootstrap's /auth/me re-hydrate OR a fresh PIN/
  // password login (KioskLoginScreen) — kick off prefetches for the
  // reference-tier data the next screens will need imminently: the active
  // product list, VAT rates, and the customer quick-pick list. These are
  // fire-and-forget (`void`, never awaited) so a slow or failing prefetch
  // can NEVER delay the login → ticket-list screen transition. Query keys
  // + fetch shapes are imported from useWbProducts.ts / KioskCustomerPickerModal.tsx
  // (not re-implemented here) so a future change to the query shape/params in
  // those files is automatically picked up here too — no drift risk.
  useEffect(() => {
    if (status !== 'authenticated') return;

    void queryClient.prefetchQuery({
      queryKey: queryKeys.wb.products({ is_active: true }),
      queryFn: () => fetchWbProducts({ is_active: true }),
      staleTime: 15 * 60 * 1000,
    });

    void queryClient.prefetchQuery({
      queryKey: queryKeys.wb.vatRates,
      queryFn: fetchWbVatRates,
      staleTime: 15 * 60 * 1000,
    });

    // Only warms the unfiltered (search === '') quick-pick view — the one
    // the picker modal queries on first open. Once the operator types
    // anything the query key changes (`search` gets added) and this
    // pre-warmed entry no longer matches, so the benefit is first-open only.
    void queryClient.prefetchQuery({
      queryKey: queryKeys.customers.list({ limit: 100 }),
      queryFn: () => fetchKioskCustomers(),
      // Must match KioskCustomerPickerModal's own staleTime (reference tier,
      // 5min) — otherwise a logout/re-login inside that window would refetch
      // data the modal's own query would still consider fresh.
      staleTime: 5 * 60_000,
    });
  }, [status, queryClient]);

  const changeWeighbridge = () => {
    clearWeighbridge();
    logout();
  };

  if (status === 'loading') {
    return (
      <KioskLayout>
        <div className={styles.kioskContent}>
          <div className={styles.centered}>
            <p className={styles.muted}>Loading…</p>
          </div>
        </div>
      </KioskLayout>
    );
  }

  if (status === 'anonymous') {
    return (
      <KioskLayout>
        <div className={styles.kioskContent}>
          <KioskLoginScreen weighbridgeEncId={weighbridgeEncId} />
        </div>
      </KioskLayout>
    );
  }

  if (!weighbridgeEncId) {
    return (
      <KioskLayout staffName={staff?.name ?? null} onLogout={logout}>
        <div className={styles.kioskContent}>
          <KioskWeighbridgeSelector />
        </div>
      </KioskLayout>
    );
  }

  return (
    <KioskAuthedSession
      weighbridgeEncId={weighbridgeEncId}
      staffName={staff?.name ?? null}
      onLogout={logout}
      onChangeWeighbridge={changeWeighbridge}
    />
  );
}
